import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { compose } from 'recompose';
import styled from 'styled-components';
import { hot } from 'react-hot-loader';
import { Link } from 'react-router-dom';

import { Card, Table, Input, Icon, Header, Dimmer, Loader, Breadcrumb, Container, Button, Label, Popup, Divider } from 'semantic-ui-react';

import { withLocale } from 'op/components/locale';
import { Evaluation } from 'op/components/evaluation';
import { withStpTests } from './stp-tests.helper';

import axios from "axios";
import { ActionBar } from '../../person/search/person-search.container';

const formatDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return 'Invalid Date';

  const dateParts = dateStr.split('/');
  const timeParts = timeStr.split(':');

  if (dateParts.length !== 3 || timeParts.length !== 2) return 'Invalid Date';

  const [day, month, year] = dateParts.map(Number);
  const [hour, minute] = timeParts.map(Number);

  const date = new Date(year, month - 1, day, hour, minute);

  return new Intl.DateTimeFormat('default', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(date);
};

const renderTest = (test, locale) => (
  <Table.Row key={test.id}>
    <Table.Cell collapsing>
      <Header as='h4' textAlign={locale._meta.rtl ? 'right' : 'left'} >
        {locale.dateTime}:
      </Header>
      {formatDateTime(test.stpTestDate, test.stpTestTime)}
    </Table.Cell>
    <Table.Cell collapsing>
      <Header as='h4' textAlign={locale._meta.rtl ? 'right' : 'left'}>
        {locale.checkedBy}:
      </Header>
      {test.stpTestCheckedBy}
    </Table.Cell>
    <Table.Cell collapsing>
      <Header as='h4' textAlign={locale._meta.rtl ? 'right' : 'left'}>
        {locale.position}:
      </Header>
      {test.stpMainPosition}
    </Table.Cell>
    <Table.Cell>
      <Header as='h4' textAlign={locale._meta.rtl ? 'right' : 'left'}>
        {locale.comments}:
      </Header>
      {test.stpMainComments}
    </Table.Cell>
    <Table.Cell collapsing>
      <Header as='h4' textAlign={locale._meta.rtl ? 'right' : 'left'}>
        {locale.results}:
      </Header>
      {
        test.results.map((result, index, arr) => (
          <React.Fragment>
            <h5 style={{ display: 'inline-block', margin: '0' }}>
              {
                result.stpTestView == 3
                  ? locale.vertical
                  : result.stpTestView == 2
                    ? locale.horizontal
                    : ''
              }
            </h5>
            <div>
              <Popup trigger={<Label color={result.stpTestResult12 === "Pass" ? "green" : "red"}>{locale.test12}</Label>} content={result.stpTestResult12} />
              <Popup trigger={<Label color={result.stpTestResult3 === "Pass" ? "green" : "red"}>{locale.test3}</Label>} content={result.stpTestResult3} />
              <Popup trigger={<Label color={result.stpTestResult4 === "Pass" ? "green" : "red"}>{locale.test4}</Label>} content={result.stpTestResult4} />
              <Popup trigger={<Label color={result.stpTestResult5 === "Pass" ? "green" : "red"}>{locale.test5}</Label>} content={result.stpTestResult5} />
              <Popup trigger={<Label color={result.stpTestResult6 === "Pass" ? "green" : "red"}>{locale.test6}</Label>} content={result.stpTestResult6} />
              {
                index < arr.length - 1 &&
                <Divider />
              }
            </div>
          </React.Fragment>
        ))
      }
    </Table.Cell>
  </Table.Row>
);

const EmptyPage = styled.div`
  display: flex;

  height: 100%;
  width: 100%;

  align-items: center;
  justify-content: center;

  padding-top: 2rem;
  padding-bottom: 10rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > h2 {
    margin: 0 !important;
  }
`;

const LoadMoreContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TableHeaderOnDesktop = styled(Table.Header)`
  @media (max-width: 767px) {
    display: none !important;
  }
`;

const StpTests = ({ locale, tests, item, loading, fetchStpTests }) => {
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchStpTests({ page });
  }, [page]);

  return (
    <Card.Group>
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Breadcrumb>
              <Breadcrumb.Section link as={Link} to="/stp">{locale.search}</Breadcrumb.Section>
              <Breadcrumb.Divider />
              <Breadcrumb.Section active>{item.item_Name}</Breadcrumb.Section>
            </Breadcrumb>
            <Loader active={loading == 'partial'} inline size='tiny' style={{ float: locale._meta.rtl ? 'left' : 'right' }} />
          </Card.Header>
          {
            tests && tests.length > 0 &&
            <div>
              <Table basic='very' celled>
                <Table.Body>
                  {tests.map(test => renderTest(test, locale))}
                </Table.Body>
              </Table>
              <LoadMoreContainer>
                <Button
                  color='blue'
                  onClick={() => setPage(page + 1)}
                  loading={loading}
                  disabled={loading}
                  content="More" />
              </LoadMoreContainer>
            </div>
          }
          {
            !(tests && tests.length > 0) &&
            <EmptyPage>
              <Header as='h2' icon>
                <Icon name='space shuttle' />
                {locale.empty.header}
                <Header.Subheader>{locale.empty.content}</Header.Subheader>
              </Header>
            </EmptyPage>
          }
        </Card.Content>
        <Dimmer active={loading == 'full'}><Loader content='Loading' /></Dimmer>
      </Card>
      <ActionBar direction={locale._meta.rtl ? 'left' : 'right'}>
        <Button icon={'plus'} disabled={false} circular
          size='huge' primary
          as={Link} to={`/stp/${item.item_ID}/tests/new`}
        />
      </ActionBar>
    </Card.Group>
  );
}

export default compose(
  hot(module),
  withLocale('stp.tests'),
  withStpTests
)(StpTests);
